<template>
    <div ref="panel" tabindex="-1" class="session-warning">
        <ModalShell
            title="Keep this scan open?"
            subtitle="For your privacy, this temporary session clears your scan results when time runs out."
            width="sm:max-w-xl sm:w-full w-full"
        >
            <template #icon><PhLock :size="24" aria-hidden="true" /></template>
            <div class="session-countdown">
                <span class="session-clock" role="timer" aria-live="off" :aria-label="`${store.sessionSecondsRemaining ?? 0} seconds remaining`">
                    {{ countdown }}
                </span>
                <span>until this session ends</span>
            </div>
            <p class="session-saved">Saved vault and family details stay.</p>
            <p v-if="store.sessionError" class="session-error" role="alert">{{ store.sessionError }}</p>
            <template #footer>
                <PrimeButton label="End session" class="p-btn-root_secondary" @click="store.endSession()" />
                <PrimeButton label="Extend session" :loading="store.sessionExtending" :disabled="store.sessionExtending" @click="store.extendSession()" />
            </template>
        </ModalShell>
    </div>
</template>

<script setup lang="ts">
import { PhLock } from '@phosphor-icons/vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useFootprintStore } from '~/stores/person/footprint';
import { useFocusTrap } from '~/composables/useFocusTrap';

const store = useFootprintStore();
const panel = ref<HTMLElement | null>(null);
const countdown = computed(() => `0:${String(store.sessionSecondsRemaining ?? 0).padStart(2, '0')}`);
// This warning has two explicit exits; Escape cannot silently dismiss the approaching expiry.
const { focus: focusWarning } = useFocusTrap(panel, computed(() => true), () => {});
// ModalShell enters its first button; the countdown needs a neutral initial focus.
onMounted(async () => {
    await nextTick();
    focusWarning();
});
</script>

<style scoped>
.session-warning { max-width: 100%; }
.session-countdown { display: flex; align-items: baseline; flex-wrap: wrap; gap: var(--space-3); color: var(--ink-body); font-size: var(--text-sm); }
.session-clock { color: var(--ink); font-size: var(--text-3xl); font-weight: var(--weight-semibold); font-variant-numeric: tabular-nums; }
.session-saved { margin-top: var(--space-2); color: var(--ink-body); font-size: var(--text-sm); }
.session-error { margin-top: var(--space-3); color: var(--danger-ink); font-size: var(--text-sm); }
</style>
